import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users, Phone, PhoneCall, CalendarCheck, AlertTriangle, Target, RefreshCw, ArrowRight,
  CheckCircle2, UserCog, Gauge, CalendarDays, Briefcase, ExternalLink,
} from 'lucide-react';
import { dashboardAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { leadNo, prioBadge } from '../lib/leadDisplay';
import TeamTargetsDrawer from '../components/TeamTargetsDrawer';

const PERIODS = [['today', 'Today'], ['week', 'This Week'], ['month', 'This Month']];
const inr = (v) => {
  const n = Number(v || 0);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};
const when = (v) => (v ? new Date(v).toLocaleString('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
}) : '—');

const Panel = ({ title, icon: Icon, iconTone = 'text-blue-600', right, children, testid, className = '' }) => (
  <section data-testid={testid} className={`clay-card flex h-full min-w-0 flex-col p-4 ${className}`}>
    <header className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
      <h2 className="flex min-w-0 items-center gap-2 font-outfit text-base font-bold text-slate-900 dark:text-slate-100">
        {Icon && <Icon className={`h-[18px] w-[18px] shrink-0 ${iconTone}`} />}
        <span className="truncate">{title}</span>
      </h2>
      <div className="flex min-w-0 max-w-full items-center gap-2">{right}</div>
    </header>
    <div className="min-w-0 flex-1">{children}</div>
  </section>
);

const Tile = ({ testid, label, value, sub, subTone, Icon, tone, pct }) => (
  <div data-testid={testid}
    className={`flex min-w-0 flex-col justify-between rounded-xl border p-2.5 sm:p-3.5 ${tone.wrap}`}>
    <span className="flex items-start gap-2 sm:gap-3">
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full sm:h-10 sm:w-10 ${tone.icon}`}>
        <Icon className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[11px] font-semibold text-slate-600 dark:text-slate-300 sm:text-[12px]">{label}</span>
        <span data-testid={`${testid}-value`}
          className="mt-0.5 block font-outfit text-[20px] font-extrabold leading-none text-slate-900 dark:text-white sm:text-[24px]">{value}</span>
      </span>
    </span>
    {pct != null && (
      <span className="mt-2 block h-1.5 w-full overflow-hidden rounded-full bg-white/70">
        <span className="block h-full rounded-full bg-emerald-500 transition-all duration-700"
          style={{ width: `${Math.max(2, Math.min(pct, 100))}%` }} />
      </span>
    )}
    {sub && <span className={`mt-1.5 block truncate text-[10px] font-medium sm:text-[11px] ${subTone || 'text-slate-500 dark:text-slate-400'}`}>{sub}</span>}
  </div>
);

const TONES = {
  blue: { wrap: 'border-[#D5E4FC] bg-[#EAF2FE] dark:bg-[#111C2E]', icon: 'bg-[#D3E3FD] text-[#1B63D8]' },
  green: { wrap: 'border-[#CFEEDC] bg-[#E9F8EF] dark:bg-[#0F2019]', icon: 'bg-[#CDEEDA] text-[#127A43]' },
  violet: { wrap: 'border-[#E1D9FB] bg-[#F1EDFD] dark:bg-[#171429]', icon: 'bg-[#E1D9FB] text-[#6A3FD1]' },
  amber: { wrap: 'border-[#FBE7C2] bg-[#FEF6E7] dark:bg-[#231C0F]', icon: 'bg-[#FBE7C2] text-[#B7761A]' },
  pink: { wrap: 'border-[#FAD9E4] bg-[#FDEEF3] dark:bg-[#26141B]', icon: 'bg-[#FAD9E4] text-[#C2185B]' },
  rose: { wrap: 'border-[#FAD5D5] bg-[#FDECEC] dark:bg-[#271416]', icon: 'bg-[#FAD5D5] text-[#D32F2F]' },
  slate: { wrap: 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40', icon: 'bg-slate-200 text-slate-700' },
};

const targetText = (r) => (r.target == null ? 'Restricted'
  : r.is_currency ? `${inr(r.achieved)} / ${inr(r.target)}`
    : r.target_type === 'conversion_rate' ? `${r.achieved}% / ${r.target}%`
      : `${r.achieved} / ${r.target}`);

export const SalesManagerDashboardPage = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [period, setPeriod] = useState('today');
  const [member, setMember] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const inflight = useRef('');

  // ONE aggregated request per (period, member) — no polling, no per-card requests.
  const load = useCallback(async (p, m, force = false) => {
    const key = `${p}|${m}`;
    if (!force && inflight.current === key) return;
    inflight.current = key;
    setLoading(true);
    try {
      const { data: res } = await dashboardAPI.getSalesManager(p, m || undefined);
      setData(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load your team dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(period, member); }, [period, member, load]);

  const team = data?.team || [];
  const me = data?.my_work || {};
  const T = data?.team_overview || {};
  const members = data?.members || [];
  const teamTargets = data?.team_targets || {};
  const pending = data?.pending || [];
  const canManageTargets = !!(data?.can_manage_targets ?? hasPermission?.('targets.manage'));
  const scopeLabel = data?.selected_user?.name || 'All My Team';

  const teamTiles = [
    { testid: 'sm-kpi-leads', label: 'Leads', value: T.leads_total ?? 0, sub: `${T.leads_open ?? 0} open · ${T.new_leads ?? 0} new`, Icon: Users, tone: TONES.blue },
    { testid: 'sm-kpi-calls', label: 'Calls', value: T.calls ?? 0, sub: `${scopeLabel}`, Icon: Phone, tone: TONES.green },
    { testid: 'sm-kpi-connected', label: 'Connected', value: T.connected ?? 0, sub: `${T.connect_rate ?? 0}% connect rate`, Icon: PhoneCall, tone: TONES.violet },
    { testid: 'sm-kpi-qualified', label: 'Qualified', value: T.qualified ?? 0, sub: 'In selected period', Icon: CheckCircle2, tone: TONES.amber },
    { testid: 'sm-kpi-followups', label: 'Follow-ups Due', value: T.followups_due ?? 0, sub: `${T.followups_today ?? 0} today`, Icon: CalendarCheck, tone: TONES.pink },
    { testid: 'sm-kpi-overdue', label: 'Overdue', value: T.overdue ?? 0, sub: (T.overdue ?? 0) > 0 ? 'Needs attention' : 'All clear', subTone: (T.overdue ?? 0) > 0 ? 'font-semibold text-rose-600' : 'font-semibold text-emerald-600', Icon: AlertTriangle, tone: TONES.rose },
    { testid: 'sm-kpi-converted', label: 'Converted', value: T.converted ?? 0, sub: 'Agreement / advance received', Icon: Briefcase, tone: TONES.green },
    { testid: 'sm-kpi-target', label: 'Target Achievement', value: `${T.target_pct ?? 0}%`, pct: T.target_pct ?? 0, sub: `${teamTargets.period_type || '—'} targets`, Icon: Target, tone: TONES.slate },
  ];

  const myTiles = [
    { testid: 'sm-my-leads', label: 'My Leads', value: me.leads_total ?? 0, sub: `${me.leads_open ?? 0} open`, Icon: Users, tone: TONES.blue },
    { testid: 'sm-my-calls', label: 'My Calls', value: me.calls ?? 0, sub: `${me.connected ?? 0} connected`, Icon: Phone, tone: TONES.green },
    { testid: 'sm-my-followups', label: 'My Follow-ups', value: me.followups_due ?? 0, sub: `${me.followups_today ?? 0} today`, Icon: CalendarCheck, tone: TONES.pink },
    { testid: 'sm-my-overdue', label: 'My Overdue', value: me.overdue ?? 0, sub: (me.overdue ?? 0) > 0 ? 'Pending action' : 'All clear', Icon: AlertTriangle, tone: TONES.rose },
    { testid: 'sm-my-qualified', label: 'My Qualified / Converted', value: `${me.qualified ?? 0} / ${me.converted ?? 0}`, sub: 'My own pipeline only', Icon: CheckCircle2, tone: TONES.amber },
    { testid: 'sm-my-target', label: 'My Target', value: `${me.target_pct ?? 0}%`, pct: me.target_pct ?? 0, sub: (me.targets || []).length ? (me.targets || []).map((r) => r.label).join(', ') : 'No target assigned', Icon: Target, tone: TONES.slate },
  ];

  const maxLoad = Math.max(1, ...members.map((m) => (m.leads_open || 0) + (m.followups_due || 0)));

  return (
    <div className="space-y-4" data-testid="sales-manager-dashboard">
      <section className="flex flex-wrap items-start justify-between gap-4" data-testid="sm-header">
        <div className="min-w-0">
          <h1 className="font-outfit text-[28px] font-extrabold leading-tight text-slate-900 dark:text-white sm:text-[32px]">
            Sales Manager Dashboard
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {user?.name} · {team.length} mapped executive{team.length === 1 ? '' : 's'} · viewing <span className="font-semibold text-slate-700 dark:text-slate-200">{scopeLabel}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
            <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
            {new Date().toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
          </span>
          {PERIODS.map(([k, label]) => (
            <button key={k} data-testid={`sm-period-${k}`} onClick={() => setPeriod(k)}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-colors ${period === k
                ? 'bg-[#0E1626] text-white shadow-sm'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'}`}>
              {label}
            </button>
          ))}
          <select value={member} onChange={(e) => setMember(e.target.value)} data-testid="sm-team-select"
            className="cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
            <option value="">All My Team</option>
            {team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button data-testid="sm-manage-targets-btn" onClick={() => setDrawer(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[#0E1626] px-3.5 py-2 text-xs font-bold text-white transition-transform hover:-translate-y-0.5">
            <UserCog className="h-3.5 w-3.5" />Targets
          </button>
          <button data-testid="sm-refresh" onClick={() => load(period, member, true)} title="Refresh"
            className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </section>

      {/* TEAM OVERVIEW */}
      <Panel testid="sm-team-overview" title={`Team Overview — ${scopeLabel}`} icon={Gauge} iconTone="text-blue-600"
        right={<span className="text-[11px] font-semibold text-slate-400">{T.members ?? 0} executive(s) in scope</span>}>
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4">
          {teamTiles.map((t) => <Tile key={t.testid} {...t} />)}
        </div>
      </Panel>

      {/* MY WORK — manager's own figures, never mixed with team */}
      <Panel testid="sm-my-work" title="My Work (Personal)" icon={Briefcase} iconTone="text-emerald-600"
        right={<span className="text-[11px] font-semibold text-slate-400">Your own leads, calls & targets only</span>}>
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-6">
          {myTiles.map((t) => <Tile key={t.testid} {...t} />)}
        </div>
      </Panel>

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {/* TEAM PERFORMANCE */}
        <Panel testid="sm-team-performance" title="Team Performance" icon={Users} iconTone="text-violet-600">
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[860px] border-separate border-spacing-0 text-xs">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-wide text-slate-400">
                  {['Executive', 'Open / Total', 'Calls / Conn.', 'Follow-ups', 'Overdue', 'Qualified', 'Converted', 'Target', ''].map((h) => (
                    <th key={h} className="border-b border-slate-200 pb-2 pr-3 font-bold dark:border-slate-800">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="sm-team-performance-body">
                {members.map((m) => (
                  <tr key={m.id} data-testid={`sm-member-row-${m.id}`} className="transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/50">
                    <td className="max-w-[160px] truncate border-b border-slate-100 py-2.5 pr-3 font-bold text-slate-800 dark:border-slate-800 dark:text-slate-100">{m.name}</td>
                    <td className="border-b border-slate-100 py-2.5 pr-3 font-semibold text-slate-600 dark:border-slate-800 dark:text-slate-300">{m.leads_open} / {m.leads_total}</td>
                    <td className="border-b border-slate-100 py-2.5 pr-3 font-semibold text-slate-600 dark:border-slate-800 dark:text-slate-300">{m.calls} / {m.connected}</td>
                    <td className="border-b border-slate-100 py-2.5 pr-3 font-semibold text-slate-600 dark:border-slate-800 dark:text-slate-300">{m.followups_due}</td>
                    <td className={`border-b border-slate-100 py-2.5 pr-3 font-bold dark:border-slate-800 ${m.overdue > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{m.overdue}</td>
                    <td className="border-b border-slate-100 py-2.5 pr-3 font-semibold text-slate-600 dark:border-slate-800 dark:text-slate-300">{m.qualified}</td>
                    <td className="border-b border-slate-100 py-2.5 pr-3 font-semibold text-slate-600 dark:border-slate-800 dark:text-slate-300">{m.converted}</td>
                    <td className="border-b border-slate-100 py-2.5 pr-3 dark:border-slate-800">
                      <span className="flex min-w-[88px] items-center gap-1.5">
                        <span className="block h-1.5 w-12 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                          <span className="block h-full rounded-full bg-emerald-500" style={{ width: `${Math.max(2, Math.min(m.target_pct || 0, 100))}%` }} />
                        </span>
                        <span className="font-bold text-slate-700 dark:text-slate-200">{m.target_pct || 0}%</span>
                      </span>
                    </td>
                    <td className="border-b border-slate-100 py-2.5 dark:border-slate-800">
                      <button data-testid={`sm-view-dashboard-${m.id}`} onClick={() => navigate(`/team/${m.id}/dashboard`)}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-blue-700 transition-colors hover:bg-blue-100">
                        View Dashboard<ExternalLink className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!members.length && (
                  <tr><td colSpan={9} className="py-6 text-center text-slate-400" data-testid="sm-team-empty">
                    {loading ? 'Loading team…' : 'No Sales Executive is mapped to you yet.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        <div className="min-w-0 space-y-4">
          {/* TEAM TARGETS */}
          <Panel testid="sm-team-targets" title="Team Targets" icon={Target} iconTone="text-emerald-600"
            right={<span className="text-[11px] font-bold text-slate-500">{teamTargets.overall_pct ?? 0}% overall</span>}>
            <ul className="space-y-2.5">
              {(teamTargets.rows || []).map((r) => (
                <li key={r.target_type} data-testid={`sm-team-target-${r.target_type}`} className="min-w-0">
                  <span className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="min-w-0 truncate font-semibold text-slate-600 dark:text-slate-300">
                      {r.label}{!r.aggregatable && <span className="ml-1 text-[10px] font-medium text-slate-400">(avg)</span>}
                    </span>
                    <span className="shrink-0 font-bold text-slate-900 dark:text-white">{targetText(r)}</span>
                  </span>
                  <span className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                    <span className="block h-full rounded-full bg-emerald-500 transition-all duration-700" style={{ width: `${Math.max(2, Math.min(r.pct, 100))}%` }} />
                  </span>
                  <span className="mt-0.5 block text-[10px] font-medium text-slate-400">{r.contributors} executive(s) with this target</span>
                </li>
              ))}
              {!(teamTargets.rows || []).length && (
                <li className="text-[11px] text-slate-400" data-testid="sm-team-targets-empty">
                  No active {teamTargets.period_type || ''} target for this team yet.
                </li>
              )}
            </ul>
            {canManageTargets && (
              <button onClick={() => setDrawer(true)} data-testid="sm-team-targets-manage"
                className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800">
                Manage team targets<ArrowRight className="h-3 w-3" />
              </button>
            )}
          </Panel>

          {/* TEAM WORKLOAD */}
          <Panel testid="sm-team-workload" title="Team Workload" icon={Gauge} iconTone="text-amber-600">
            <ul className="space-y-3">
              {members.map((m) => {
                const total = (m.leads_open || 0) + (m.followups_due || 0);
                return (
                  <li key={m.id} data-testid={`sm-workload-${m.id}`} className="min-w-0">
                    <span className="flex items-center justify-between gap-2 text-[11px]">
                      <span className="min-w-0 truncate font-semibold text-slate-700 dark:text-slate-200">{m.name}</span>
                      <span className="shrink-0 font-bold text-slate-900 dark:text-white">
                        {m.leads_open} open · {m.followups_due} due
                        {m.overdue > 0 && <span className="ml-1 text-rose-600">· {m.overdue} overdue</span>}
                      </span>
                    </span>
                    <span className="mt-1 block h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                      <span className={`block h-full rounded-full ${m.overdue > 0 ? 'bg-amber-500' : 'bg-blue-500'}`}
                        style={{ width: `${Math.max(3, (total / maxLoad) * 100)}%` }} />
                    </span>
                  </li>
                );
              })}
              {!members.length && <li className="text-[11px] text-slate-400" data-testid="sm-workload-empty">No workload to show.</li>}
            </ul>
          </Panel>
        </div>
      </div>

      {/* PRIORITY PENDING WORK */}
      <Panel testid="sm-pending-work" title="Priority Pending Work" icon={AlertTriangle} iconTone="text-rose-600"
        right={<span className="text-[11px] font-semibold text-slate-400">
          {data?.pending_total ?? 0} pending · {data?.pending_overdue ?? 0} overdue
        </span>}>
        <div className="min-w-0 overflow-x-auto">
          <table className="w-full min-w-[880px] border-separate border-spacing-0 text-xs">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wide text-slate-400">
                {['Lead No', 'Client / Lead', 'Owner', 'Priority', 'Status', 'Next Follow-up', 'State', 'Action'].map((h) => (
                  <th key={h} className="border-b border-slate-200 pb-2 pr-3 font-bold dark:border-slate-800">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="sm-pending-body">
              {pending.map((r) => {
                const [pLabel, pTone] = prioBadge(r.priority);
                return (
                  <tr key={r.id} data-testid={`sm-pending-row-${r.id}`} className="transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/50">
                    <td className="whitespace-nowrap border-b border-slate-100 py-2.5 pr-3 font-mono font-semibold text-primary dark:border-slate-800">{leadNo(r)}</td>
                    <td className="max-w-[160px] truncate border-b border-slate-100 py-2.5 pr-3 font-bold text-slate-800 dark:border-slate-800 dark:text-slate-100">{r.client}</td>
                    <td className="max-w-[140px] truncate border-b border-slate-100 py-2.5 pr-3 text-slate-500 dark:border-slate-800">{r.owner}</td>
                    <td className="border-b border-slate-100 py-2.5 pr-3 dark:border-slate-800">
                      <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${pTone}`}>{pLabel}</span>
                    </td>
                    <td className="whitespace-nowrap border-b border-slate-100 py-2.5 pr-3 capitalize text-slate-500 dark:border-slate-800">{(r.status || '—').replace(/_/g, ' ')}</td>
                    <td className="whitespace-nowrap border-b border-slate-100 py-2.5 pr-3 text-slate-600 dark:border-slate-800 dark:text-slate-300">{when(r.next_follow_up)}</td>
                    <td className={`whitespace-nowrap border-b border-slate-100 py-2.5 pr-3 font-bold dark:border-slate-800 ${r.is_overdue ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {r.is_overdue ? 'Overdue' : 'Due'}
                    </td>
                    <td className="border-b border-slate-100 py-2.5 dark:border-slate-800">
                      <button data-testid={`sm-open-lead-${r.lead_id}`} onClick={() => navigate(`/leads/${r.lead_id}`)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300">
                        Open<ArrowRight className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!pending.length && (
                <tr><td colSpan={8} className="py-6 text-center text-slate-400" data-testid="sm-pending-empty">
                  {loading ? 'Loading pending work…' : 'No pending follow-up in this scope.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <TeamTargetsDrawer open={drawer} onClose={() => setDrawer(false)} team={team}
        canManage={canManageTargets} onChanged={() => load(period, member, true)} />
    </div>
  );
};

export default SalesManagerDashboardPage;
